import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { put, type Destination, type Profile, type SetupState, type User } from '../api';
import { currentLang, t } from '../i18n';

/**
 * Changes the first-run guide state on the server (PUT /api/me/setup) and puts
 * the returned user into the `me` cache. The guide is shown or hidden only
 * after the server answers: on an error it stays where it was.
 */
export function useSetupState() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<SetupState | null>(null);
  const [error, setError] = useState('');
  const [failed, setFailed] = useState<SetupState | null>(null);
  const set = async (state: SetupState) => {
    setBusy(state);
    setError('');
    setFailed(null);
    try {
      const user = await put<User>('/api/me/setup', { state });
      // Merge, not replace: an older center answers a plain User without area / admin_url /
      // app_url, and losing them put the full admin menu on the user name (owner, 18.10).
      qc.setQueryData<User | null>(['me'], (old) => (old ? { ...old, ...user } : user));
      return true;
    } catch (e) {
      setError((e as Error).message);
      setFailed(state);
      return false;
    } finally {
      setBusy(null);
    }
  };
  return { set, busy, error, retry: () => (failed ? set(failed) : Promise.resolve(false)) };
}

/** Tiles of the guide: featured profiles, in the order the API lists them. */
export const featured = (profiles: Profile[] | undefined) => (profiles ?? []).filter((p) => p.featured);

/** The destination a tile stands for: the oldest of its profile (the one source=setup returns). */
export function destinationOf(profile: string, dests: Destination[] | undefined): Destination | undefined {
  return (dests ?? []).filter((d) => d.profile === profile).sort((a, b) => a.id - b.id)[0];
}

export type TileState = 'idle' | 'added' | 'keyed';

export function tileState(d: Destination | undefined): TileState {
  if (!d) return 'idle';
  return d.needs_key ? 'added' : 'keyed';
}

/** "www.instagram.com" → "instagram.com" for the "Open {domain} ↗" button. */
export function domainOf(url: string | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** "1920x1080" + 60 → "1080p60". */
export function videoLabel(resolution?: string, fps?: number): string | undefined {
  if (!resolution) return undefined;
  const m = /^(\d+)x(\d+)$/.exec(resolution);
  const f = fps ? Math.round(fps) : undefined;
  if (!m) return f ? `${resolution} ${f} fps` : resolution;
  return `${m[2]}p${f ?? ''}`;
}

/** "AAC-LC 48000 Hz 2ch" → "AAC 48k". */
export function audioLabel(format?: string): string | undefined {
  if (!format) return undefined;
  const m = /(\d+)\s*Hz/.exec(format);
  const rate = m ? `${Math.round(Number(m[1]) / 100) / 10}k` : '';
  return /AAC/i.test(format) ? `AAC${rate ? ' ' + rate : ''}` : format;
}

/** Sample rate in Hz from the audio format, if the node reported it. */
export function audioRate(format?: string): number | undefined {
  const m = format && /(\d+)\s*Hz/.exec(format);
  return m ? Number(m[1]) : undefined;
}

/**
 * The key steps of a profile in the cabinet's language. The center sends them in EN; profiles
 * of the first vertical release with their own keys in i18n.ts (TikTok, VERT-PLAT) get ES / RU
 * from there, as the HLS hints do (ProtocolSettings). Others stay as the center sends them.
 */
export function profileSteps(p: Profile): string[] | undefined {
  if (p.id === 'tiktok' && currentLang() !== 'en' && p.key_steps?.length === 5) {
    return [t('profile.tiktok.step1'), t('profile.tiktok.step2'), t('profile.tiktok.step3'), t('profile.tiktok.step4'), t('profile.tiktok.step5')];
  }
  return p.key_steps;
}

/** The platform shows the server address with the key (TikTok): no default url to fill in. */
export const serverFromPlatform = (p: Profile) => !!p.server_from_platform;

/** The platform shows 9:16 video: its outputs get the vertical input (input auto, docs/vertical.md §3.1). */
export const portrait = (p: Profile) => p.orientation === 'portrait';
